// Main application component with routing - 72 lines ✅
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AppLayout } from '@/components/layout/AppLayout'
import { ErrorBoundary } from '@/components/layout/ErrorBoundary'
import { LoadingSpinner } from '@/components/layout/LoadingSpinner'
import { Login } from '@/pages/Login'
import { Dashboard } from '@/pages/Dashboard'
import { IssueRecording } from '@/pages/IssueRecording'
import { KnowledgeBase } from '@/pages/KnowledgeBase'
import { Documents } from '@/pages/Documents'
import { Settings } from '@/pages/Settings'
import { Issues } from '@/pages/Issues'
import { IssueDetail } from '@/pages/IssueDetail'
import { SystemArchitecture } from '@/pages/SystemArchitecture'
import { useAuth } from '@/hooks/useAuth'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function ProtectedRoute({ children }: { children: React.ReactNode }): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner message="Loading..." />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

export function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/issues" element={<Issues />} />
              <Route path="/issues/new" element={<IssueRecording />} />
              <Route path="/issues/:id" element={<IssueDetail />} />
              <Route path="/knowledge" element={<KnowledgeBase />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/architecture" element={<SystemArchitecture />} />
              <Route path="/settings" element={<Settings />} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
